import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import generateBarcode from '../../../../components/barcodeHelper';
import { header } from '../../../../components/header.encry';
import ProductCategoryService from '../../../../components/ProductCategoryService';
import { ProductSelectionItem } from '../../types';
import { resolveProductImageUrl } from '../../hooks/useInventoryProducts';
import { parsePrice } from '../../utils/formatters';
import SquareImageCropModal from '../../../../components/SquareImageCropModal';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
const PRODUCT_DIALOG_Z_INDEX = 2400;
const PRODUCT_DIALOG_MENU_Z_INDEX = PRODUCT_DIALOG_Z_INDEX + 10;

interface AddInternalProductDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (product?: ProductSelectionItem) => void;
  initialImageUrl?: string | null;
  initialGalleryImageUrls?: string[];
  initialProductData?: {
    name?: string | null;
    description?: string | null;
    long_description?: string | null;
  } | null;
}

interface FormState {
  name: string;
  description: string;
  long_description: string;
  regular_price: string;
  discounted_price: string;
  url_product: string;
  gallery_images: string[];
  category: string;
  isComposant: boolean;
}

const defaultFormState: FormState = {
  name: '',
  description: '',
  long_description: '',
  regular_price: '',
  discounted_price: '',
  url_product: '',
  gallery_images: [],
  category: '',
  isComposant: false,
};

interface CategoryOption {
  id: string;
  name: string;
  type: string | undefined;
  enable: boolean;
}

const safeGenerateBarcode = () => {
  try {
    if (typeof document !== 'undefined') {
      return generateBarcode()?.barcodeValue ?? '';
    }
  } catch (error) {
    console.warn('Barcode generation failed', error);
  }
  return `${Date.now()}`;
};

const imageUrlToFile = async (imageUrl: string, fallbackName: string) => {
  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch image (${response.status})`);
  }

  const blob = await response.blob();
  const fileNameFromUrl = imageUrl.split('/').pop()?.split('?')[0] || fallbackName;
  const hasExtension = /\.[a-z0-9]+$/i.test(fileNameFromUrl);
  const mimeExtension = blob.type?.split('/')[1] || 'jpg';
  const fileName = hasExtension ? fileNameFromUrl : `${fileNameFromUrl}.${mimeExtension}`;

  return new File([blob], fileName, {
    type: blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
};

const AddInternalProductDialog: React.FC<AddInternalProductDialogProps> = ({
  open,
  onClose,
  onCreated,
  initialImageUrl,
  initialGalleryImageUrls = [],
  initialProductData,
}) => {
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [syncDescriptionWithName, setSyncDescriptionWithName] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [pendingCropFile, setPendingCropFile] = useState<File | null>(null);
  const [pendingGalleryCropFiles, setPendingGalleryCropFiles] = useState<File[]>([]);
  const [cropTarget, setCropTarget] = useState<'primary' | 'gallery'>('primary');
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageError, setImageError] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [galleryPreviewUrls, setGalleryPreviewUrls] = useState<string[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');

  const resetForm = useCallback(() => {
    setFormState({ ...defaultFormState });
    setError('');
    setPictureFile(null);
    setGalleryFiles([]);
    setPendingCropFile(null);
    setPendingGalleryCropFiles([]);
    setCropTarget('primary');
    setCropModalOpen(false);
    setImageError('');
    setSyncDescriptionWithName(true);
  }, []);

  const isValid = useMemo(() => {
    return (
      formState.name.trim().length > 0 &&
      formState.description.trim().length > 0 &&
      formState.regular_price.trim().length > 0 &&
      formState.category.trim().length > 0
    );
  }, [formState]);

  const handleChange = <K extends keyof FormState>(
    field: K,
    value: FormState[K]
  ) => {
    setFormState((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'name') {
        if (syncDescriptionWithName || !prev.description.trim().length) {
          next.description = (value as string) ?? '';
        }
      }
      if (field === 'description') {
        next.description = (value as string) ?? '';
      }
      return next;
    });
    if (field === 'description') {
      const stringValue = typeof value === 'string' ? value : '';
      setSyncDescriptionWithName(!stringValue.trim());
    }
  };

  const resetImageInput = useCallback(() => {
    const input = document.querySelector<HTMLInputElement>('input[type="file"][data-add-product-image]');
    if (input) {
      input.value = '';
    }
  }, []);
  const resetGalleryInput = useCallback(() => {
    const input = document.querySelector<HTMLInputElement>('input[type="file"][data-add-product-gallery]');
    if (input) {
      input.value = '';
    }
  }, []);
  const normalizeImageFileToSquare = useCallback(async (file: File) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
          return;
        }
        reject(new Error('Invalid image data.'));
      };
      reader.onerror = () => reject(new Error('Unable to read image.'));
      reader.readAsDataURL(file);
    });

    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Unable to load image.'));
      img.src = dataUrl;
    });

    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    const squareSize = Math.max(width, height);
    const canvas = document.createElement('canvas');
    canvas.width = squareSize;
    canvas.height = squareSize;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas unavailable.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, squareSize, squareSize);
    context.drawImage(
      image,
      Math.floor((squareSize - width) / 2),
      Math.floor((squareSize - height) / 2),
      width,
      height
    );

    const mimeType = file.type && file.type.startsWith('image/')
      ? file.type
      : 'image/jpeg';

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((nextBlob) => {
        if (nextBlob) {
          resolve(nextBlob);
          return;
        }
        reject(new Error('Unable to export normalized image.'));
      }, mimeType, 0.95);
    });

    return new File([blob], file.name, {
      type: blob.type || mimeType,
      lastModified: Date.now(),
    });
  }, []);

  const handlePictureSelection = useCallback((file: File | null) => {
    if (!file) {
      setPictureFile(null);
      setPendingCropFile(null);
      setImageError('');
      return;
    }

    const reader = new FileReader();
    const img = new Image();

    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;

        if (!width || !height) {
          setImageError("Impossible de lire l'image sélectionnée.");
          resetImageInput();
          return;
        }

        if (width < 500 || height < 500) {
          setImageError("L'image doit mesurer au minimum 500 x 500 pixels.");
          setPictureFile(null);
          resetImageInput();
          return;
        }

        if (Math.abs(width - height) >= 1) {
          setCropTarget('primary');
          setPendingCropFile(file);
          setCropModalOpen(true);
          setImageError('');
          return;
        }

        Promise.resolve(
          Math.abs(width - height) < 1 ? file : normalizeImageFileToSquare(file)
        )
          .then((normalizedFile) => {
            setPictureFile(normalizedFile);
            setPendingCropFile(null);
            setCropModalOpen(false);
            setImageError('');
          })
          .catch(() => {
            setImageError("Impossible de convertir l'image en format carrÃ©.");
            setPictureFile(null);
            setPendingCropFile(null);
            setCropModalOpen(false);
            resetImageInput();
          });
      };
      img.onerror = () => {
        setImageError("Impossible de charger l'image sélectionnée.");
        resetImageInput();
      };
      img.src = url;
    };
    reader.onerror = () => {
      setImageError("Impossible de lire l'image sélectionnée.");
      resetImageInput();
    };

    reader.readAsDataURL(file);
  }, [normalizeImageFileToSquare, resetImageInput]);
  const handleGallerySelection = useCallback(async (files: File[]) => {
    if (!files.length) {
      setGalleryFiles([]);
      return;
    }

    setCropTarget('gallery');
    setPendingCropFile(files[0]);
    setPendingGalleryCropFiles(files.slice(1));
    setCropModalOpen(true);
    setImageError('');
    if (typeof window === 'undefined') {
    try {
      const normalizedFiles = await Promise.all(
        files.map(async (file) => {
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              if (typeof reader.result === 'string') {
                resolve(reader.result);
                return;
              }
              reject(new Error('Invalid image data.'));
            };
            reader.onerror = () => reject(new Error('Unable to read image.'));
            reader.readAsDataURL(file);
          });

          const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Unable to load image.'));
            img.src = dataUrl;
          });

          const width = image.naturalWidth || image.width;
          const height = image.naturalHeight || image.height;

          if (!width || !height) {
            throw new Error("Impossible de lire l'image sÃ©lectionnÃ©e.");
          }

          if (width < 500 || height < 500) {
            throw new Error("L'image doit mesurer au minimum 500 x 500 pixels.");
          }

          return Math.abs(width - height) < 1
            ? file
            : normalizeImageFileToSquare(file);
        })
      );

      setGalleryFiles(normalizedFiles);
    } catch (error: any) {
      toast.error(error?.message || "Impossible de convertir la galerie en format carrÃ©.");
      setGalleryFiles([]);
      resetGalleryInput();
    }
    }
  }, [normalizeImageFileToSquare, resetGalleryInput]);

  const handleClose = () => {
    resetForm();
    resetGalleryInput();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    let active = true;
    const loadCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError('');
      try {
        const response = await ProductCategoryService.fetchCategories();
        const items = Array.isArray(response?.response)
          ? response.response
          : response;
        const normalized = (Array.isArray(items) ? items : [])
          .map((item: any) => ({
            id: String(item?._id ?? item?.id ?? item?.value ?? ''),
            name: item?.name ?? 'Sans nom',
            type: item?.type,
            enable: typeof item?.enable === 'boolean' ? item.enable : true,
          }))
          .filter((item): item is CategoryOption => Boolean(item.id));
        const filtered = normalized.filter(
          (category) =>
            category.type === 'pegasus' && (category.enable ?? true)
        );
        if (active) {
          setCategories(filtered);
        }
      } catch (categoryError: any) {
        const message =
          categoryError?.response?.data?.message ||
          categoryError?.message ||
          'Erreur lors du chargement des catégories';
        if (active) {
          setCategoriesError(message);
          toast.error(message);
        }
      } finally {
        if (active) {
          setCategoriesLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextName = typeof initialProductData?.name === 'string'
      ? initialProductData.name
      : '';
    const nextDescription = typeof initialProductData?.description === 'string'
      ? initialProductData.description
      : '';
    const nextLongDescription = typeof initialProductData?.long_description === 'string'
      ? initialProductData.long_description
      : '';

    setFormState((prev) => ({
      ...prev,
      name: nextName,
      description: nextDescription || nextName,
      long_description: nextLongDescription,
    }));
    setSyncDescriptionWithName(!nextDescription.trim());
  }, [initialProductData, open]);

  useEffect(() => {
    if (!open || !initialImageUrl) {
      return;
    }

    let active = true;

    const preloadInitialImage = async () => {
      setFormState((prev) => (
        prev.url_product === initialImageUrl
          ? prev
          : { ...prev, url_product: initialImageUrl }
      ));

      try {
        const file = await imageUrlToFile(initialImageUrl, 'processed-image.jpg');

        if (active) {
          handlePictureSelection(file);
        }
      } catch (prefillError) {
        console.warn('Unable to preload initial product image', prefillError);
      }
    };

    preloadInitialImage();

    return () => {
      active = false;
    };
  }, [handlePictureSelection, initialImageUrl, open]);

  useEffect(() => {
    if (!open || !initialGalleryImageUrls.length) {
      return;
    }

    let active = true;

    const preloadInitialGalleryImages = async () => {
      try {
        const files = await Promise.all(
          initialGalleryImageUrls
            .filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim())
            .map((imageUrl, index) => imageUrlToFile(imageUrl, `processed-gallery-${index + 1}.jpg`))
        );

        if (active && files.length) {
          setGalleryFiles(files);
        }
      } catch (prefillError) {
        console.warn('Unable to preload initial product gallery images', prefillError);
        if (active) {
          setFormState((prev) => ({
            ...prev,
            gallery_images: initialGalleryImageUrls.filter(Boolean),
          }));
        }
      }
    };

    preloadInitialGalleryImages();

    return () => {
      active = false;
    };
  }, [initialGalleryImageUrls, open]);

  useEffect(() => {
    if (pictureFile) {
      const previewUrl = URL.createObjectURL(pictureFile);
      setImagePreviewUrl(previewUrl);

      return () => {
        URL.revokeObjectURL(previewUrl);
      };
    }

    setImagePreviewUrl(resolveProductImageUrl(formState.url_product) ?? '');
    return undefined;
  }, [formState.url_product, pictureFile]);
  useEffect(() => {
    if (!galleryFiles.length) {
      setGalleryPreviewUrls([]);
      return undefined;
    }

    const previewUrls = galleryFiles.map((file) => URL.createObjectURL(file));
    setGalleryPreviewUrls(previewUrls);

    return () => {
      previewUrls.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    };
  }, [galleryFiles]);

  const handleCropCancel = () => {
    setPendingCropFile(null);
    setPendingGalleryCropFiles([]);
    setCropModalOpen(false);
    if (cropTarget === 'gallery') {
      resetGalleryInput();
      return;
    }
    resetImageInput();
  };

  const handleCropComplete = (file: File) => {
    if (cropTarget === 'gallery') {
      setGalleryFiles((prev) => [...prev, file]);
      const [nextFile, ...remainingFiles] = pendingGalleryCropFiles;
      setPendingGalleryCropFiles(remainingFiles);
      setPendingCropFile(nextFile || null);
      setCropModalOpen(Boolean(nextFile));
      if (!nextFile) {
        resetGalleryInput();
      }
      setImageError('');
      return;
    }

    setPictureFile(file);
    setPendingCropFile(null);
    setCropModalOpen(false);
    setImageError('');
  };
  const uploadProductImageFiles = useCallback(async ({
    productName,
    price,
    description,
    files,
  }: {
    productName: string;
    price: string;
    description: string;
    files: File[];
  }) => {
    const uploadedFiles: string[] = [];

    for (const file of files) {
      const uploadData = new FormData();
      uploadData.append('productName', productName);
      uploadData.append('price', price);
      uploadData.append('description', description);
      uploadData.append('picture', file);

      const uploadResponse = await axios.post(
        'https://office.le-celadon.ma/uploadimagesgetUrl.php',
        uploadData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!uploadResponse?.data?.success) {
        throw new Error(
          uploadResponse?.data?.error || 'TÃ©lÃ©versement de lâ€™image Ã©chouÃ©'
        );
      }

      uploadedFiles.push(uploadResponse.data.filename ?? '');
    }

    return uploadedFiles.filter(Boolean);
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!API_URL) {
      setError('API non configurée');
      return;
    }
    if (!isValid) {
      setError('Veuillez renseigner les champs obligatoires');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const trimmedName = formState.name.trim();
      const trimmedDescription =
        formState.description.trim() || trimmedName;
      const trimmedLongDescription = formState.long_description.trim();
      const trimmedRegularPrice = formState.regular_price.trim();
      const trimmedDiscountedPrice = formState.discounted_price.trim();
      const trimmedCategory = formState.category.trim();
      const fallbackImageUrl = formState.url_product;
      const barcode = safeGenerateBarcode();
      let imageUrl = formState.url_product;
      let galleryImages = Array.isArray(formState.gallery_images)
        ? [...formState.gallery_images]
        : [];

      if (pictureFile) {
        const uploadData = new FormData();
        uploadData.append('productName', trimmedName);
        uploadData.append('price', trimmedRegularPrice);
        uploadData.append('description', trimmedDescription);
        uploadData.append('picture', pictureFile);

        const uploadResponse = await axios.post(
          'https://office.le-celadon.ma/uploadimagesgetUrl.php',
          uploadData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (!uploadResponse?.data?.success) {
          throw new Error(
            uploadResponse?.data?.error || 'Téléversement de l’image échoué'
          );
        }

        imageUrl = uploadResponse.data.filename ?? '';
      }

      if (galleryFiles.length) {
        galleryImages = await uploadProductImageFiles({
          productName: trimmedName,
          price: trimmedRegularPrice,
          description: trimmedDescription,
          files: galleryFiles,
        });
      }

      const payload = {
        name: trimmedName,
        description: trimmedDescription,
        ...(trimmedLongDescription
          ? { long_description: trimmedLongDescription }
          : {}),
        regular_price: trimmedRegularPrice,
        discounted_price: trimmedDiscountedPrice || '',
        category: trimmedCategory,
        url_product: imageUrl || '',
        gallery_images: galleryImages,
        barcode,
        type: 'internal',
        availability: true,
        details: [],
        stores: [],
        isComposant: formState.isComposant,
      };
      const response = await axios.post(
        `${API_URL}/products`,
        payload,
        header()
      );
      const productData = response?.data?.product ?? response?.data;
      if (productData) {
        toast.success('Produit interne ajouté');
        const rawProductName = productData.name?.toString().trim();
        const rawProductDescription = productData.description
          ?.toString()
          .trim();
        const normalizedName = rawProductName || rawProductDescription || trimmedName;
        const normalizedDescription =
          rawProductDescription || rawProductName || trimmedDescription;
        const resolvedImage =
          resolveProductImageUrl(
            productData.url_product ??
              productData.imageUrl ??
              productData.image ??
              fallbackImageUrl
          ) ?? undefined;
        const normalizedDiscountedPrice = [
          productData.discounted_price,
          trimmedDiscountedPrice,
        ]
          .map((value) => parsePrice(value))
          .find((value): value is number => value !== undefined);
        const normalizedRegularPrice = [
          productData.regular_price,
          trimmedRegularPrice,
        ]
          .map((value) => parsePrice(value))
          .find((value): value is number => value !== undefined);
        const normalizedFallbackPrice = [
          productData.price,
          productData.discounted_price,
          trimmedRegularPrice,
          trimmedDiscountedPrice,
        ]
          .map((value) => parsePrice(value))
          .find((value): value is number => value !== undefined);
        const normalizedPrice =
          normalizedDiscountedPrice ?? normalizedRegularPrice ?? normalizedFallbackPrice;

        const normalized: ProductSelectionItem = {
          id: String(productData._id ?? productData.id ?? ''),
          name: normalizedName,
          description: normalizedDescription,
          barcode: productData.barcode ?? barcode,
          sku: productData.sku ?? productData.reference ?? '',
          type: productData.type ?? 'internal',
          imageUrl: resolvedImage,
          isComposant:
            typeof productData.isComposant === 'boolean'
              ? productData.isComposant
              : formState.isComposant,
          price: normalizedPrice,
          regularPrice: normalizedRegularPrice,
          discountedPrice: normalizedDiscountedPrice,
        };
        resetForm();
        resetGalleryInput();
        onCreated(normalized);
      } else {
        toast.success('Produit interne ajouté');
        resetForm();
        resetGalleryInput();
        const normalizedDiscountedPrice = parsePrice(
          trimmedDiscountedPrice || undefined
        );
        const normalizedRegularPrice = parsePrice(trimmedRegularPrice || undefined);
        onCreated({
          id: barcode,
          name: trimmedName,
          description: trimmedDescription || undefined,
          barcode,
          type: 'internal',
          imageUrl: resolveProductImageUrl(fallbackImageUrl) ?? undefined,
          isComposant: formState.isComposant,
          price: normalizedDiscountedPrice ?? normalizedRegularPrice,
          regularPrice: normalizedRegularPrice,
          discountedPrice: normalizedDiscountedPrice,
        });
      }
    } catch (submitError: any) {
      const message =
        submitError?.response?.data?.message ||
        submitError?.response?.data?.error ||
        submitError?.message ||
        'Erreur lors de la création du produit';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        // Keep the shared product dialog above any Bootstrap modal that opened it.
        zIndex: PRODUCT_DIALOG_Z_INDEX,
      }}
    >
      <DialogTitle>Ajouter un produit interne</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Les champs nom, description et prix sont requis.
        </Typography>
        <Stack
          component="form"
          spacing={2}
          onSubmit={handleSubmit}
        >
          <TextField
            label="Nom"
            value={formState.name}
            onChange={(event) => handleChange('name', event.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={formState.description}
            onChange={(event) => handleChange('description', event.target.value)}
            required
            multiline
            minRows={3}
            fullWidth
            helperText={
              syncDescriptionWithName
                ? 'Par défaut, la description reprend le nom saisi.'
                : undefined
            }
          />
          <TextField
            label="Description longue"
            value={formState.long_description}
            onChange={(event) => handleChange('long_description', event.target.value)}
            multiline
            minRows={5}
            fullWidth
            helperText="Optionnel. Peut contenir du HTML."
          />
          <TextField
            label="Prix"
            type="number"
            value={formState.regular_price}
            onChange={(event) => handleChange('regular_price', event.target.value)}
            required
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
          />
          <TextField
            label="Prix remisé"
            type="number"
            value={formState.discounted_price}
            onChange={(event) => handleChange('discounted_price', event.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
          />
          <TextField
            select
            label="Catégorie"
            value={formState.category}
            onChange={(event) => handleChange('category', event.target.value)}
            fullWidth
            required
            disabled={categoriesLoading}
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                sx: {
                  zIndex: PRODUCT_DIALOG_MENU_Z_INDEX,
                },
                PaperProps: {
                  sx: {
                    zIndex: PRODUCT_DIALOG_MENU_Z_INDEX,
                  },
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              Sélectionner une catégorie
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
          {categoriesLoading && (
            <FormHelperText>Chargement des catégories…</FormHelperText>
          )}
          {categoriesError && (
            <FormHelperText error>{categoriesError}</FormHelperText>
          )}
          <FormControlLabel
            control={(
              <Checkbox
                checked={formState.isComposant}
                onChange={(event) =>
                  handleChange('isComposant', event.target.checked)
                }
              />
            )}
            label="C'est un composant (isComposant)"
          />
          <Stack spacing={1}>
            <Button variant="outlined" component="label">
              {pictureFile ? 'Changer l’image' : 'Ajouter une image'}
              <input
                hidden
                accept="image/*"
                type="file"
                data-add-product-image
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  handlePictureSelection(file);
                }}
              />
            </Button>
            {pictureFile && (
              <FormHelperText>{pictureFile.name}</FormHelperText>
            )}
            {imagePreviewUrl && (
              <Stack spacing={1}>
                <FormHelperText>Aperçu</FormHelperText>
                <img
                  src={imagePreviewUrl}
                  alt="Aperçu du produit"
                  style={{
                    width: '100%',
                    maxWidth: 220,
                    maxHeight: 220,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                  }}
                />
              </Stack>
            )}
            {imageError && (
              <FormHelperText error>{imageError}</FormHelperText>
            )}
          </Stack>
          <Stack spacing={1}>
            <Button variant="outlined" component="label">
              {galleryFiles.length ? 'Ajouter plus d’images à la galerie' : 'Ajouter des images de galerie'}
              <input
                hidden
                accept="image/*"
                type="file"
                data-add-product-gallery
                multiple
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  handleGallerySelection(files);
                }}
              />
            </Button>
            {galleryFiles.length > 0 && (
              <FormHelperText>
                {galleryFiles.length} image(s) sÃ©lectionnÃ©e(s)
              </FormHelperText>
            )}
            {(galleryPreviewUrls.length > 0 || formState.gallery_images.length > 0) && (
              <Stack spacing={1}>
                <FormHelperText>Galerie</FormHelperText>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(galleryPreviewUrls.length > 0
                    ? galleryPreviewUrls
                    : formState.gallery_images.map((image) => resolveProductImageUrl(image) ?? '')
                  )
                    .filter(Boolean)
                    .map((image, index) => (
                      <img
                        key={`${image}-${index}`}
                        src={image}
                        alt={`Galerie ${index + 1}`}
                        style={{
                          width: 90,
                          height: 90,
                          objectFit: 'cover',
                          borderRadius: 8,
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                        }}
                      />
                    ))}
                </Stack>
              </Stack>
            )}
          </Stack>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={handleClose}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={submitting || !isValid}>
              Ajouter
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fermer</Button>
      </DialogActions>
      <SquareImageCropModal
        open={cropModalOpen}
        file={pendingCropFile}
        onCancel={handleCropCancel}
        onComplete={handleCropComplete}
      />
    </Dialog>
  );
};

export default AddInternalProductDialog;
